import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ApprovalQueue from '../components/ApprovalQueue';
import DecisionLogViewer from '../components/DecisionLogViewer';
import CoachingLogViewer from '../components/CoachingLogViewer';
import { getDashboardStats, getPendingApprovals, getRecentLogs, getCoachingLogs, getTickets } from '../utils/api';

const TICKET_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'escalated', label: 'Pending approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'more_info_requested', label: 'More info requested' },
  { value: 'closed', label: 'Closed' },
  { value: 'evidence_collection', label: 'Evidence collection' },
  { value: 'triage_complete', label: 'Triage complete' },
];

function Dashboard() {
  const [activeTab, setActiveTab] = useState('approvals');
  const [stats, setStats] = useState(null);
  const [pendingTickets, setPendingTickets] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [ticketStatusFilter, setTicketStatusFilter] = useState('');
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [logs, setLogs] = useState([]);
  const [coachingLogs, setCoachingLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const loadDashboardData = async () => {
    setError(null);
    setLoading(true);
    try {
      const [statsRes, approvalsRes, logsRes] = await Promise.all([
        getDashboardStats(),
        getPendingApprovals(),
        getRecentLogs(50),
      ]);
      setStats(statsRes);
      setPendingTickets(approvalsRes.tickets || []);
      setLogs(logsRes.logs || []);
      try {
        const coachingRes = await getCoachingLogs(50);
        setCoachingLogs(coachingRes.logs || []);
      } catch (coachingErr) {
        console.warn('Coaching logs unavailable:', coachingErr.message);
        setCoachingLogs([]);
      }
    } catch (err) {
      console.error('Dashboard load failed:', err);
      setError(err.message || 'Could not load dashboard. Check that the backend is running.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadAllTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const params = { limit: 200 };
      if (ticketStatusFilter) params.status = ticketStatusFilter;
      const res = await getTickets(params);
      setAllTickets(res.tickets || []);
    } catch (err) {
      console.error('Failed to load tickets:', err);
      setAllTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  }, [ticketStatusFilter]);

  useEffect(() => {
    if (activeTab === 'tickets') loadAllTickets();
  }, [activeTab, loadAllTickets]);

  const handleTicketClick = (ticketId) => {
    navigate(`/dashboard/ticket/${ticketId}`);
  };

  const handleApprovalComplete = () => {
    // Refresh data after an approval action
    loadDashboardData();
  };

  return (
    <div className="page-content">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Back-Office Dashboard</h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
          Supervisor view — Maria Chen (SUP-0042)
        </p>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-card__value">{stats.total_tickets}</div>
            <div className="stat-card__label">Total Tickets</div>
          </div>
          <div className="stat-card">
            <div className="stat-card__value" style={{ color: 'var(--color-warning)' }}>
              {stats.pending_approval}
            </div>
            <div className="stat-card__label">Pending Approval</div>
          </div>
          <div className="stat-card">
            <div className="stat-card__value" style={{ color: 'var(--color-safe)' }}>
              {stats.approved_today}
            </div>
            <div className="stat-card__label">Approved Today</div>
          </div>
          <div className="stat-card">
            <div className="stat-card__value">
              {(stats.average_confidence * 100).toFixed(0)}%
            </div>
            <div className="stat-card__label">Avg Confidence</div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
        <button
          className={`btn btn--sm ${activeTab === 'approvals' ? 'btn--primary' : 'btn--outline'}`}
          onClick={() => setActiveTab('approvals')}
        >
          Approval Queue ({pendingTickets.length})
        </button>
        <button
          className={`btn btn--sm ${activeTab === 'tickets' ? 'btn--primary' : 'btn--outline'}`}
          onClick={() => setActiveTab('tickets')}
        >
          All Tickets
        </button>
        <button
          className={`btn btn--sm ${activeTab === 'logs' ? 'btn--primary' : 'btn--outline'}`}
          onClick={() => setActiveTab('logs')}
        >
          Decision Logs
        </button>
        <button
          className={`btn btn--sm ${activeTab === 'coaching' ? 'btn--primary' : 'btn--outline'}`}
          onClick={() => setActiveTab('coaching')}
        >
          Coaching Logs ({coachingLogs.length})
        </button>
      </div>

      {/* Tab Content */}
      {loading && !stats && !error ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
          Loading dashboard...
        </div>
      ) : error ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, maxWidth: 480, margin: '0 auto' }}>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>{error}</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
            Ensure the backend is running at <code>http://localhost:8000</code> and refresh.
          </p>
          <button type="button" className="btn btn--primary" onClick={loadDashboardData}>
            Retry
          </button>
        </div>
      ) : (
        <>
          {activeTab === 'approvals' && (
            <ApprovalQueue
              tickets={pendingTickets}
              onTicketClick={handleTicketClick}
              onApprovalComplete={handleApprovalComplete}
            />
          )}
          {activeTab === 'tickets' && (
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <label htmlFor="dashboard-ticket-filter" style={{ fontSize: 13, fontWeight: 600 }}>Filter by status</label>
                <select
                  id="dashboard-ticket-filter"
                  className="form-input"
                  value={ticketStatusFilter}
                  onChange={(e) => setTicketStatusFilter(e.target.value)}
                  style={{ minWidth: 180, maxWidth: 240 }}
                >
                  {TICKET_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <button type="button" className="btn btn--sm btn--outline" onClick={loadAllTickets} disabled={loadingTickets}>
                  {loadingTickets ? 'Loading…' : 'Refresh'}
                </button>
              </div>
              {loadingTickets ? (
                <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>Loading tickets…</div>
              ) : allTickets.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                  No tickets match the filter.
                </div>
              ) : (
                <table className="table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Ticket</th>
                      <th>Equipment</th>
                      <th>Status</th>
                      <th>Risk</th>
                      <th>Approved by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allTickets.map((t) => (
                      <tr
                        key={t.ticket_id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleTicketClick(t.ticket_id)}
                      >
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{t.ticket_id}</td>
                        <td>{t.equipment_model} — {t.fault_code}</td>
                        <td>
                          <span className={`badge badge--${(t.triage_risk_level || 'medium').toLowerCase()}`}>
                            {t.status}
                          </span>
                        </td>
                        <td>{t.triage_risk_level || '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t.approved_by || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          {activeTab === 'logs' && (
            <DecisionLogViewer logs={logs} />
          )}
          {activeTab === 'coaching' && (
            <CoachingLogViewer logs={coachingLogs} />
          )}
        </>
      )}
    </div>
  );
}

export default Dashboard;
